import { style, state, animate, transition, trigger } from '@angular/animations';
import { isPlatformServer, formatNumber } from '@angular/common';
import * as i0 from '@angular/core';
import { PLATFORM_ID, inject, Injectable, InjectionToken, reflectComponentType, Directive, Input, HostBinding, HostListener, TemplateRef, LOCALE_ID, Pipe } from '@angular/core';
import * as DetectGrid from 'detect-grid';
import * as i1 from '@angular/router';
import * as i2 from '@angular/platform-browser';
import { Duration } from 'luxon';

// see styles/themes/base/_duration.scss
var Speed;
(function (Speed) {
    Speed[Speed["Fast"] = 200] = "Fast";
    Speed[Speed["Normal"] = 400] = "Normal";
    Speed[Speed["Slow"] = 600] = "Slow";
})(Speed || (Speed = {}));

const expandCollapse = trigger('expandCollapse', [
    state('*', style({
        height: '0',
    })),
    state('expanded', style({
        height: '*',
    })),
    state('collapsed', style({
        height: '0',
        display: 'none',
    })),
    transition('* => *', [
        style({
            display: '*',
        }),
        animate(Speed.Fast),
    ]),
]);

var AspectRatio;
(function (AspectRatio) {
    AspectRatio["Original"] = "original";
    AspectRatio["Square"] = "1x1";
    AspectRatio["Standard"] = "16x9";
    AspectRatio["Wide"] = "384x143";
    AspectRatio["Ultrawide"] = "636x175";
})(AspectRatio || (AspectRatio = {}));

// See _breakpoints.scss
var Breakpoint;
(function (Breakpoint) {
    Breakpoint[Breakpoint["ExtraSmall"] = 320] = "ExtraSmall";
    Breakpoint[Breakpoint["Small"] = 480] = "Small";
    Breakpoint[Breakpoint["MediumSmall"] = 768] = "MediumSmall";
    Breakpoint[Breakpoint["Medium"] = 900] = "Medium";
    Breakpoint[Breakpoint["MediumLarge"] = 1080] = "MediumLarge";
    Breakpoint[Breakpoint["Large"] = 1440] = "Large";
    Breakpoint[Breakpoint["Larger"] = 1680] = "Larger";
    Breakpoint[Breakpoint["ExtraLarge"] = 1920] = "ExtraLarge";
})(Breakpoint || (Breakpoint = {}));
function lower(breakpoint) {
    return `(max-width: ${breakpoint}px)`;
}
function greater(breakpoint) {
    return `(min-width: ${breakpoint + 1}px)`;
}

/**
 * This method correctly handles varying month lengths & leap years
 */
function addMonths(readonlyDate, months) {
    const date = new Date(readonlyDate.valueOf());
    const d = date.getDate();
    date.setMonth(date.getMonth() + months);
    if (date.getDate() != d) {
        date.setDate(0);
    }
    return date;
}
/**
 * This method correctly handles varying month lengths & leap years
 */
function addYears(readonlyDate, years) {
    const date = new Date(readonlyDate.valueOf());
    const d = date.getDate();
    date.setFullYear(date.getFullYear() + years);
    if (date.getDate() != d) {
        date.setDate(0);
    }
    return date;
}

const UP_ARROW = 'ArrowUp';
const DOWN_ARROW = 'ArrowDown';
const LEFT_ARROW = 'ArrowLeft';
const RIGHT_ARROW = 'ArrowRight';
const TAB = 'Tab';
const ENTER = 'Enter';
const ESCAPE = 'Escape';
const SPACE = ' ';
const BACKSPACE = 'Backspace';
const HOME = 'Home';
const END = 'End';
const PAGE_UP = 'PageUp';
const PAGE_DOWN = 'PageDown';
function hasModifierKey(event) {
    return event.altKey || event.shiftKey || event.ctrlKey || event.metaKey;
}

var ColumnWidth;
(function (ColumnWidth) {
    ColumnWidth[ColumnWidth["Full"] = 12] = "Full";
    ColumnWidth[ColumnWidth["Wider"] = 10] = "Wider";
    ColumnWidth[ColumnWidth["Wide"] = 8] = "Wide";
    ColumnWidth[ColumnWidth["Half"] = 6] = "Half";
    ColumnWidth[ColumnWidth["Narrow"] = 4] = "Narrow";
    ColumnWidth[ColumnWidth["OneFourth"] = 3] = "OneFourth";
    ColumnWidth[ColumnWidth["OneSixth"] = 2] = "OneSixth";
    ColumnWidth[ColumnWidth["OneTwelth"] = 1] = "OneTwelth";
})(ColumnWidth || (ColumnWidth = {}));
// See _layout.scss
const breakpointColumns = {
    'default': 4,
    [greater(Breakpoint.MediumSmall)]: 12,
};

function getScrollParent(element) {
    if (element == null) {
        return window.document.documentElement;
    }
    if (window.getComputedStyle(element).overflow !== 'visible') {
        return element;
    }
    else {
        return getScrollParent(element.parentElement);
    }
}
function getScrollOffset(element, scrollParent = null) {
    if (scrollParent === null) {
        scrollParent = getScrollParent(element);
    }
    const elementRect = element.getBoundingClientRect();
    const scrollParentRect = scrollParent.getBoundingClientRect();
    return {
        left: elementRect.left - scrollParentRect.left,
        top: elementRect.top - scrollParentRect.top,
        right: scrollParentRect.right - elementRect.right,
        bottom: scrollParentRect.bottom - elementRect.bottom,
    };
}
/**
 * Chromium does not do two smooth scrollings at the same time with the native scrollIntoView function
 */
function scrollToElement(element, options = {}) {
    options.scrollParent = options.scrollParent ?? getScrollParent(element);
    options.behavior = options.behavior ?? 'smooth';
    options.axis = options.axis ?? 'both';
    const scrollOffset = getScrollOffset(element, options.scrollParent);
    const scrollTo = {};
    if (options.axis === 'x' || options.axis === 'both') {
        if (scrollOffset.left < 0 || scrollOffset.right < 0) {
            scrollTo.left = options.scrollParent.scrollLeft + Math.min(scrollOffset.left, 0) - Math.min(scrollOffset.right, 0);
        }
    }
    if (options.axis === 'y' || options.axis === 'both') {
        if (scrollOffset.bottom < 0 || scrollOffset.top < 0) {
            scrollTo.top = options.scrollParent.scrollTop + Math.min(scrollOffset.top, 0) - Math.min(scrollOffset.bottom, 0);
        }
    }
    if (scrollTo.left !== undefined || scrollTo.top !== undefined) {
        options.scrollParent.scrollTo({
            ...scrollTo,
            behavior: options.behavior,
        });
    }
}

"use strict";

function serverDetectGrid(element) {
    const rootColumns = breakpointColumns[greater(Breakpoint.MediumSmall)];
    let row = 1;
    let gridColumn = 0;
    let column = 0;
    let lastChildElement = null;
    let lastRowElements = [];
    for (let i = 0; i < element.children.length; i++) {
        const childElement = element.children[i];
        const childColumnsString = childElement.getAttribute('kb-grid-item-columns');
        const childColumns = childColumnsString ? parseInt(childColumnsString) : 12;
        if (gridColumn + childColumns > rootColumns) {
            gridColumn = childColumns;
            column = 1;
            row++;
            if (lastChildElement) {
                lastChildElement.setAttribute('data-last-col', '');
                if (lastRowElements.length === 1) {
                    lastChildElement.setAttribute('data-single-col', '');
                }
            }
            lastRowElements = [];
        }
        else {
            gridColumn += childColumns;
            column++;
        }
        if (column === 1) {
            childElement.setAttribute('data-first-col', '');
        }
        if (row === 1) {
            childElement.setAttribute('data-first-row', '');
        }
        childElement.setAttribute('data-nth-row', row.toString());
        childElement.setAttribute('data-nth-col', column.toString());
        lastChildElement = childElement;
        lastRowElements.push(childElement);
    }
    for (const lastRowElement of lastRowElements) {
        lastRowElement.setAttribute('data-last-row', '');
    }
    if (lastRowElements.length === 1) {
        lastRowElements[0].setAttribute('data-single-col', '');
    }
    if (lastRowElements.length > 0) {
        lastRowElements[lastRowElements.length - 1].setAttribute('data-last-col', '');
    }
}
/**
 * This service mimics the CSS Selectors Level 4 Grid-Structural-Selectors https://www.w3.org/TR/selectors-4/#table-pseudos
 */
class GridStructuralSelectorsService {
    constructor() {
        this.registeredLists = new Map();
        this.platformId = inject(PLATFORM_ID);
    }
    updateColumnsAndRows(element) {
        if (isPlatformServer(this.platformId)) {
            serverDetectGrid(element);
            return;
        }
        DetectGrid.markGrid(element);
    }
    // additional css is not always loaded immediately, so we need sometimes need to wait and test for a known style
    static async awaitStyleValue(element, knownProperty, knownPropertyValue) {
        if (window.getComputedStyle(element).getPropertyValue(knownProperty) === knownPropertyValue) {
            return;
        }
        return new Promise((resolve) => {
            setTimeout(async () => {
                await this.awaitStyleValue(element, knownProperty, knownPropertyValue);
                resolve();
            }, 50);
        });
    }
    async attach(element, mediaQuery) {
        if (isPlatformServer(this.platformId)) {
            this.updateColumnsAndRows(element);
            return;
        }
        const entry = {
            listener: () => {
                this.updateColumnsAndRows(element);
            },
            mediaQueryList: window.matchMedia(mediaQuery),
        };
        this.registeredLists.set(element, entry);
        entry.mediaQueryList.addEventListener('change', entry.listener);
        await GridStructuralSelectorsService.awaitStyleValue(element, 'display', 'grid');
        this.updateColumnsAndRows(element);
    }
    detach(element) {
        if (isPlatformServer(this.platformId)) {
            return;
        }
        if (!this.registeredLists.has(element)) {
            return;
        }
        const entry = this.registeredLists.get(element);
        entry?.mediaQueryList.removeEventListener('change', entry.listener);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: GridStructuralSelectorsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: GridStructuralSelectorsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: GridStructuralSelectorsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const COMPONENT_FACTORY_SERVICE_CONFIGURATION_TOKEN = new InjectionToken('kb:component-factory-service-configuration');
function provideComponentFactoryConfiguration(configuration) {
    return {
        provide: COMPONENT_FACTORY_SERVICE_CONFIGURATION_TOKEN, useValue: configuration,
    };
}
class ComponentFactoryService {
    constructor() {
        this.configuration = inject(COMPONENT_FACTORY_SERVICE_CONFIGURATION_TOKEN);
        this.componentMirrors = new Map();
        for (const [name, componentType] of this.configuration.components) {
            const componentMirror = reflectComponentType(componentType);
            if (componentMirror === null) {
                continue;
            }
            this.componentMirrors.set(name, componentMirror);
        }
    }
    async getComponentMirrorByName(name) {
        if (this.componentMirrors.has(name)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return Promise.resolve(this.componentMirrors.get(name));
        }
        const componentType = this.configuration.lazyComponents.find(component => component[0] === name);
        if (componentType === undefined) {
            return null;
        }
        return reflectComponentType(await componentType[2]());
    }
    async getComponentMirrorByElement(element) {
        for (const componentMirror of this.componentMirrors.values()) {
            if (element.matches(componentMirror.selector)) {
                return componentMirror;
            }
        }
        for (const [, selector, componentType] of this.configuration.lazyComponents) {
            if (element.matches(selector)) {
                return reflectComponentType(await componentType());
            }
        }
        return null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: ComponentFactoryService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: ComponentFactoryService, providedIn: 'any' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: ComponentFactoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'any', // This is needed so that each feature module can declare it's own set of configured components
                }]
        }], ctorParameters: () => [] });

class HtmlParserBrowserService {
    constructor() {
        this.browserDomParser = new DOMParser();
    }
    textContent(html) {
        return this.parseFromString(html).body.textContent || '';
    }
    parseFromString(html) {
        return this.browserDomParser.parseFromString(html, 'text/html');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: HtmlParserBrowserService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: HtmlParserBrowserService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: HtmlParserBrowserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const HTML_PARSER_SERVICE_TOKEN = new InjectionToken('HtmlParserService');
function provideHtmlParserService(service) {
    return {
        provide: HTML_PARSER_SERVICE_TOKEN,
        useClass: service,
    };
}

class NativeInputDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: NativeInputDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.7", type: NativeInputDirective, isStandalone: true, selector: "input", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: NativeInputDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'input',
                    standalone: true,
                }]
        }] });

class NativeTextareaDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: NativeTextareaDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.7", type: NativeTextareaDirective, isStandalone: true, selector: "textarea", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: NativeTextareaDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'textarea',
                    standalone: true,
                }]
        }] });

class RouterOrExternalLinkDirective {
    constructor(router, domSanitizer) {
        this.router = router;
        this.domSanitizer = domSanitizer;
        this.safeProtocols = ['callto'];
        this.href = null;
        this.urlSchemeRegex = new RegExp('^[a-z0-9]+:', 'i');
    }
    ngOnChanges() {
        if (this.kbRouterOrExternalLink) {
            const safeProtocol = this.safeProtocols.find((value) => this.kbRouterOrExternalLink?.startsWith(`${value}:`));
            this.href = safeProtocol ? this.domSanitizer.bypassSecurityTrustUrl(this.kbRouterOrExternalLink) : this.kbRouterOrExternalLink;
        }
        else {
            this.href = null;
        }
    }
    onClick(button, ctrlKey, metaKey, shiftKey, targetElement) {
        const href = targetElement.getAttribute('href');
        if (href === null || href === undefined) {
            return true;
        }
        // Do not invoke router on right click, ctrl, meta or shift click
        if (button !== 0 || ctrlKey || metaKey || shiftKey) {
            return true;
        }
        const target = targetElement.getAttribute('target');
        // Do not invoke router if the target is anything except _self
        if (target && target !== '_self') {
            return true;
        }
        // Do not invoke the router if the url is scheme relative
        if (href.startsWith('//')) {
            return true;
        }
        // Do not invoke the router if the url has a scheme
        if (this.urlSchemeRegex.test(href)) {
            return true;
        }
        // At this point the url is guaranteed to point to this application
        // Invoke the angular router
        this.router.navigateByUrl(href);
        // Prevent normal browser navigation
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: RouterOrExternalLinkDirective, deps: [{ token: i1.Router }, { token: i2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.7", type: RouterOrExternalLinkDirective, isStandalone: true, selector: "[kbRouterOrExternalLink]", inputs: { safeProtocols: "safeProtocols", kbRouterOrExternalLink: "kbRouterOrExternalLink" }, host: { listeners: { "click": "onClick($event.button,$event.ctrlKey,$event.metaKey,$event.shiftKey,$event.target)" }, properties: { "attr.href": "this.href" } }, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: RouterOrExternalLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[kbRouterOrExternalLink]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: i1.Router }, { type: i2.DomSanitizer }], propDecorators: { safeProtocols: [{
                type: Input
            }], kbRouterOrExternalLink: [{
                type: Input
            }], href: [{
                type: HostBinding,
                args: ['attr.href']
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event.button', '$event.ctrlKey', '$event.metaKey', '$event.shiftKey', '$event.target']]
            }] } });

class TemplateTypeDirective {
    constructor() {
        this.contentTemplate = inject((TemplateRef));
    }
    // this magic is how we tell Angular the context type for this directive, which then propagates down to the type of the template
    static ngTemplateContextGuard(dir, ctx) {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: TemplateTypeDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.7", type: TemplateTypeDirective, isStandalone: true, selector: "ng-template[kbTemplateType]", inputs: { typeToken: ["kbTemplateType", "typeToken"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: TemplateTypeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ng-template[kbTemplateType]',
                    standalone: true,
                }]
        }], propDecorators: { typeToken: [{
                type: Input,
                args: [{ alias: 'kbTemplateType', required: true }]
            }] } });

// units in uppercase are the only correct form.´
// alternatives are:
// kB -> Metric not 1024 based but 1000 based
// KiB -> kibibyte -> 1024 based
// KB -> Legacy name of kilobyte 1024 based (used in Windows)
// https://en.wikipedia.org/wiki/Kilobyte
const byteUnits = [
    ['PB', Math.pow(1024, 5)],
    ['TB', Math.pow(1024, 4)],
    ['GB', 1 << 30],
    ['MB', 1 << 20],
    ['KB', 1 << 10],
    ['B', 1],
];
const fallbackByteUnit = byteUnits[byteUnits.length - 1];
class BytesPipe {
    constructor() {
        this.localeId = inject(LOCALE_ID);
    }
    transform(bytes) {
        const positiveBytes = Math.abs(bytes);
        let unit = fallbackByteUnit;
        for (const currentUnit of byteUnits) {
            if (positiveBytes >= currentUnit[1]) {
                unit = currentUnit;
                break;
            }
        }
        const bytesInUnit = bytes / unit[1];
        return `${formatNumber(bytesInUnit, this.localeId, '1.0-1')} ${unit[0]}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: BytesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: BytesPipe, isStandalone: true, name: "bytes" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: BytesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'bytes',
                    standalone: true,
                }]
        }] });

class DurationPipe {
    transform(duration, unit = 'seconds') {
        return Duration.fromObject({
            [unit]: duration,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: DurationPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: DurationPipe, isStandalone: true, name: "duration" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: DurationPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'duration',
                    standalone: true,
                }]
        }] });

class FormatDurationPipe {
    transform(duration, format, options) {
        return duration
            .toFormat(format, options);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormatDurationPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: FormatDurationPipe, isStandalone: true, name: "formatDuration" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: FormatDurationPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'formatDuration',
                    standalone: true,
                }]
        }] });

class HumanDurationPipe {
    constructor() {
        this.localeId = inject(LOCALE_ID);
    }
    transform(duration, unitDisplay = 'narrow', listStyle = 'narrow', units = ['hours', 'minutes', 'seconds']) {
        return duration
            .reconfigure({ locale: this.localeId })
            .shiftTo(...units)
            .mapUnits((x) => Math.round(x))
            .toHuman({ listStyle: listStyle, unitDisplay: unitDisplay });
        // TODO: remove leading and trailing zero units
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: HumanDurationPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.7", ngImport: i0, type: HumanDurationPipe, isStandalone: true, name: "humanDuration" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: HumanDurationPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'humanDuration',
                    standalone: true,
                }]
        }] });

/*
 * Public API Surface of knorr-bremse-components
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AspectRatio, BACKSPACE, Breakpoint, BytesPipe, ColumnWidth, ComponentFactoryService, DOWN_ARROW, DurationPipe, END, ENTER, ESCAPE, FormatDurationPipe, GridStructuralSelectorsService, HOME, HTML_PARSER_SERVICE_TOKEN, HtmlParserBrowserService, HumanDurationPipe, LEFT_ARROW, NativeInputDirective, NativeTextareaDirective, PAGE_DOWN, PAGE_UP, RIGHT_ARROW, RouterOrExternalLinkDirective, SPACE, Speed, TAB, TemplateTypeDirective, UP_ARROW, addMonths, addYears, breakpointColumns, expandCollapse, getScrollOffset, getScrollParent, greater, hasModifierKey, lower, provideComponentFactoryConfiguration, provideHtmlParserService, scrollToElement };
